import React from "react";
import PropTypes from "prop-types";
import styled, { withTheme } from "styled-components";
import { cloud, silver } from "@vitalsource/vstui/colors";
import { TextField, Button, Form } from "@vitalsource/vstui";
import Dispatcher from "../../../dispatcher";
import MenuHeader from "../../ui_elements/menu-header";
import { UnsafeHtml } from "../../../ui/components/mixins";
import Printometer from "./printometer";
import PrintHistory from "./print-history";
import validateRange from "./validate-range";
import executePrintJob from "./execute-print-job";

const Container = styled.div`
  width: 400px;
`;

const Text = styled.span`
  color: ${silver.getShade(10)};
  font-weight: 300;
  line-height: 1.5;
`;
const LifetimeMessage = styled(Text)`
  padding: 6px;
  padding-bottom: 16px;
`;

const Section = styled.div`
  padding: 10px 10px 16px 10px;
  border-bottom: 1px solid ${cloud.getShade(4)};
  display: flex;
  flex-direction: column;
  min-height: 120px;
`;

const Center = styled.div`
  text-align: center;
`;

const PrintForm = styled(Form)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ErrorMessagePlaceholder = styled.div`
  height: 16px;
  width: 100%;
  margin: 5px 0;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.vstui.color.error};
  font-size: 1.3rem;
  font-weight: 400;
  margin: 5px 0;
`;

const PageInput = styled(TextField)`
  margin: 0;
  width: auto;
`;

const ToWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 44px;
  margin: 0 10px;
  font-weight: 700;
`;

const PrintButton = styled(Button)`
  margin-left: 10px;
`;

class PrintRange extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
      from: props.currentPage,
      to: props.currentPage,
      totalPagesPrinted: props.totalPagesPrinted,
      printButtonClicked: false,
      printId: Fraction.Helpers.generateGUID(), // generate a new one on every print request
      simulations: {}, // { printId: totalPages }
      printing: {} // { printId: totalPages}
    };

    this.handleRef = this.handleRef.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleValidate = this.handleValidate.bind(this);
    this.handlePrintDone = this.handlePrintDone.bind(this);
    this.handlePrintFailed = this.handlePrintFailed.bind(this);
    this.simulatePrint = this.simulatePrint.bind(this);
    this.printPages = this.printPages.bind(this);
    this.getCurrentPageRange = this.getCurrentPageRange.bind(this);
  }

  componentDidMount() {
    this.simulatePrint();
    Fraction.Reporter.gaEvent("reader/options/print-page-range");
    Dispatcher.bind(Dispatcher.PRINT_DONE, this.handlePrintDone);
    Dispatcher.bind(Dispatcher.PRINT_FAILED, this.handlePrintFailed);
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.PRINT_DONE, this.handlePrintDone);
    Dispatcher.unbind(Dispatcher.PRINT_FAILED, this.handlePrintFailed);
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen) {
      if (!prevProps.isOpen) {
        this.setState({ errorMessage: null });
      }
      if (
        (this.state.from === this.state.to &&
          prevProps.currentPage !== this.props.currentPage) ||
        prevProps.isOpen !== this.props.isOpen
      ) {
        this.setState(
          {
            from: this.props.currentPage,
            to: this.props.currentPage
          },
          this.simulatePrint
        );
      }
      if (
        prevProps.lifetimePrintPageCount !==
          this.props.lifetimePrintPageCount ||
        prevProps.totalPagesPrinted !== this.props.totalPagesPrinted
      ) {
        this.setState(
          {
            totalPagesPrinted: this.props.totalPagesPrinted
          },
          this.simulatePrint
        );
      }
    }
  }

  handlePrintFailed({ printId }) {
    this.setState(({ printing: { [printId]: _removing, ...printing } }) => ({
      printing
    }));
  }

  handlePrintDone({ printId }) {
    if (printId) {
      this.setState(
        ({
          printing: { [printId]: request, ...printing },
          totalPagesPrinted
        }) => ({
          totalPagesPrinted: totalPagesPrinted + request.totalPages,
          printing
        })
      );
    }
  }

  getCurrentPageRange(from = this.state.from, to = this.state.to) {
    const { totalPagesPrinted } = this.state;
    const { isbn, copies, printLimit, lifetimePrintPageCount } = this.props;
    return validateRange(
      isbn,
      from,
      to,
      copies,
      totalPagesPrinted,
      printLimit,
      lifetimePrintPageCount
    );
  }

  handleRef(node) {
    this.form = node;
  }

  simulatePrint() {
    const range = this.getCurrentPageRange();
    if (range.error) {
      this.setState(state => ({
        simulations: {
          ...state.simulations,
          [state.printId]: range.error.pages.length
        }
      }));
    } else if (range.pages) {
      this.setState(state => ({
        simulations: {
          ...state.simulations,
          [state.printId]: range.pages.length
        }
      }));
    }
  }

  printPages() {
    const { from, to, printId } = this.state;
    const { isbn, theme } = this.props;
    const range = this.getCurrentPageRange(from, to);
    if (range.error) {
      this.setState(state => ({
        simulations: {
          ...state.simulations,
          [printId]: range.error.pages.length
        }
      }));
    } else {
      this.setState(
        ({
          simulations: { [printId]: _removing, ...simulations },
          printing
        }) => ({
          printId: Fraction.Helpers.generateGUID(),
          simulations,
          printing: {
            ...printing,
            [printId]: { totalPages: range.pages.length, from, to }
          }
        })
      );
      executePrintJob(theme, printId, isbn, from, to);
    }
  }

  handleChange(event) {
    const { value, name } = event.currentTarget;
    this.setState({ [name]: value, errorMessage: null }, this.simulatePrint);
  }

  handleSubmit(event) {
    event.preventDefault();
    const validation = this.getCurrentPageRange();

    if (validation.error && validation.error.level === "form") {
      this.setState({ errorMessage: validation.error.reason }, () => {
        if (this.formError) {
          this.formError.focus();
        }
      });
    } else {
      this.printPages();
    }
  }

  handleValidate(field) {
    return new Promise(resolve => {
      const validation = this.getCurrentPageRange();

      resolve(
        validation.error &&
          validation.error.level === "input" &&
          validation.error[field]
          ? validation.error.reason
          : ""
      );
    });
  }

  renderPrintometer() {
    const { lifetimePrintPageCount } = this.props;
    const { simulations, printing, from, to, totalPagesPrinted } = this.state;

    const totalPrinting = Object.values(printing).reduce(
      (acc, { totalPages }) => acc + totalPages,
      0
    );

    const totalSimulated = Object.keys(simulations)
      .filter(
        () =>
          !Object.values(printing).some(
            req => req.from === from && req.to === to
          )
      )
      .reduce((acc, printId) => acc + simulations[printId], 0);

    return (
      <Center>
        <Printometer
          pending={totalPrinting + totalSimulated}
          current={totalPagesPrinted}
          total={lifetimePrintPageCount}
        />
      </Center>
    );
  }

  renderRange() {
    const { printLimit } = this.props;
    const { from, to, errorMessage } = this.state;

    return (
      <Section>
        <Text>
          <b>{i18n.t("print.enter-pages")}</b>
          {printLimit > 0
            ? ` (${i18n.t("print.limit-per-page", { limit: printLimit })})`
            : ` (${i18n.t("print.recommendation")})`}
        </Text>

        {errorMessage ? (
          <ErrorMessage
            tabIndex={-1}
            innerRef={node => (this.formError = node)}
          >
            {errorMessage}
          </ErrorMessage>
        ) : (
          <ErrorMessagePlaceholder />
        )}

        <PrintForm onRef={this.handleRef} onSubmit={this.handleSubmit}>
          <PageInput
            id="fromInput"
            innerRef={node => (this.from = node)}
            label={i18n.t("base.from")}
            placeholder={i18n.t("base.start").capitalize()}
            name="from"
            value={from}
            onChange={this.handleChange}
            onBlur={() => this.from && this.from.validate()}
            validate={() => this.handleValidate("from")}
            size="large"
            type="text"
            labelHidden
            fullWidth
            inheritStyles
          />

          <ToWrapper>{i18n.t("base.to")}</ToWrapper>

          <PageInput
            id="toInput"
            innerRef={node => (this.to = node)}
            label={i18n.t("base.to")}
            placeholder={i18n.t("base.end").capitalize()}
            name="to"
            value={to}
            onChange={this.handleChange}
            onBlur={() => this.to && this.to.validate()}
            validate={() => this.handleValidate("to")}
            size="large"
            type="text"
            labelHidden
            fullWidth
            inheritStyles
          />

          <PrintButton type="submit" size="large">
            {i18n.t("print.print")}
          </PrintButton>
        </PrintForm>
      </Section>
    );
  }

  render() {
    const {
      lifetimePrintLimit,
      printLimit,
      isbn,
      isOpen,
      lifetimePrintPageCount,
      totalPagesPrinted
    } = this.props;

    return (
      <Container>
        <MenuHeader>{i18n.t("print.print")}</MenuHeader>

        {lifetimePrintLimit < 100 && (
          <Section>
            <LifetimeMessage>
              <UnsafeHtml>
                {i18n.t("print.lifetime-message", {
                  limit: this.props.lifetimePrintPageCount
                })}
              </UnsafeHtml>
            </LifetimeMessage>
            {this.renderPrintometer()}
          </Section>
        )}

        {isOpen && printLimit >= -1 && this.renderRange()}

        <PrintHistory
          isbn={isbn}
          isOpen={isOpen}
          expanded={totalPagesPrinted >= lifetimePrintPageCount}
        />
      </Container>
    );
  }
}

PrintRange.defaultProps = {
  lifetimePrintPageCount: 0,
  totalPagesPrinted: 0
};

PrintRange.propTypes = {
  isbn: PropTypes.string.isRequired,
  currentPage: PropTypes.string,
  lifetimePrintPageCount: PropTypes.number,
  totalPagesPrinted: PropTypes.number,
  printLimit: PropTypes.number,
  copies: PropTypes.object
};

export default withTheme(PrintRange);
